import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { useSubscription } from "@/hooks/useSubscription";

export const Route = createFileRoute("/checkout/success")({
  component: CheckoutSuccessPage,
  head: () => ({
    meta: [
      { title: "You're in! — JobYoda" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function CheckoutSuccessPage() {
  const { refetch, isActive, subscription } = useSubscription();

  // Webhooks are async — poll briefly until subscription appears.
  useEffect(() => {
    try {
      localStorage.removeItem("plan_choice_pending");
      sessionStorage.removeItem("pending_checkout");
    } catch {
      /* ignore */
    }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      await refetch();
      if (!isActive && attempts < 10) {
        setTimeout(tick, 1500);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default", p: 2 }}>
      <Container maxWidth="sm" disableGutters>
        <Card>
          <CardContent sx={{ p: { xs: 4, sm: 6 }, textAlign: "center" }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              You're in!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              {isActive
                ? "Your subscription is active. Premium features are unlocked."
                : "We're activating your subscription. This usually takes a few seconds."}
            </Typography>
            <Button component={Link} to="/app/profile" variant="contained" size="large">
              Go to your dashboard
            </Button>
            {subscription?.product_id && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: "block" }}>
                Plan: {subscription.product_id}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
